import { EmailData } from '../../../models/email/EmailData';
import { LogType } from '../../../models/logs/LogType.enum';
import { Location } from '../../../models/assets-data/LocationConfig.interface';
import { parseRecipients } from './contacts.rules';

export type BuildEmailDataFailure = 'locationNotFound' | 'noLogType' | 'noRecipients';

export type BuildEmailDataResult =
  | { ok: true; emailData: EmailData }
  | { ok: false; reason: BuildEmailDataFailure };

/**
 * Builds the notification email data for a receiving log submission.
 *
 * Fails (without side effects) when the selected location can't be resolved,
 * no log type is set, or there is no possible recipient (no notify list and
 * no location default emails). The caller decides how to surface failures.
 *
 * @param params.notifyListString - Comma-separated notify emails from the log entry.
 * @param params.locations - Configured storage locations (provides address + default emails).
 * @param params.selectedLocationLabel - Label of the user-selected location.
 * @param params.logType - The log type (Project/Service), or null if undetermined.
 * @param params.userEmail - Email of the submitting user.
 */
export function buildEmailData(params: {
  notifyListString: string;
  locations: Location[];
  selectedLocationLabel: string | undefined;
  logType: LogType | null;
  userEmail: string;
}): BuildEmailDataResult {
  const recipients = parseRecipients(params.notifyListString);

  const location = params.locations.find(
    (loc) => loc.locationLabel === params.selectedLocationLabel
  );
  if (!location) {
    return { ok: false, reason: 'locationNotFound' };
  }

  if (!params.logType) {
    return { ok: false, reason: 'noLogType' };
  }

  if (recipients.length === 0 && !location.serviceEmails && !location.projectEmails) {
    return { ok: false, reason: 'noRecipients' };
  }

  return {
    ok: true,
    emailData: new EmailData({
      address: location.address || '',
      logType: params.logType,
      recipients: recipients,
      serviceEmails: location.serviceEmails || [],
      projectEmails: location.projectEmails || [],
      userEmail: params.userEmail || '',
    }),
  };
}
